import * as React from 'react';
import { useApolloClient } from '@apollo/client';
import {
  CheckCircleIcon,
  EllipsisHorizontalCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/solid';
import * as Sentry from '@sentry/nextjs';
import {
  createUserWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  sendEmailVerification,
} from 'firebase/auth';
import { useRouter } from 'next/router';
import { SIGNUP_COMPLETED_EVENT } from 'constants/analytics';
import { AuthStatus, SocialAuthProvider } from 'constants/auth';
import {
  COACH_HOME_PAGE,
  COACH_HOME_WITH_QUICK_ONBOARD_PAGE,
  LOGIN_PAGE,
  ROOT_PAGE,
} from 'constants/pages';
import { ErrorResponse, RequestStatus } from 'constants/requests';
import {
  AccountType,
  MINIMUM_PASSWORD_LENGTH,
  getIsValidPasswordFormat,
  getIsValidUsernameCaseIncensitiveFormat,
  getIsValidUsernameFormat,
} from 'constants/user';
import {
  GetExistingUserQuery,
  useCheckUsernameAvailabilityLazyQuery,
  useGetClosestCitiesLazyQuery,
  useGetGroupByAccessCodeLazyQuery,
  useInsertSignupRequestMutation,
  useInsertUserGroupMembershipMutation,
} from 'types/generated/client';
import { identify, trackEvent } from 'services/client/analytics';
import { facebookTrackCompleteRegistration } from 'services/client/analytics/facebook';
import { auth } from 'services/client/firebase';
import { pollForNewUser } from 'services/client/pollForNewUser';
import { getIsNativePlatform } from 'utils/mobile/getIsNativePlatform';
import { getPlatform } from 'utils/mobile/getPlatform';
import { detectFraudFromBrowser } from 'utils/shared/fraud/detectFraud';
import { getSignupErrorMessage } from 'utils/shared/getSignupErrorMessage';
import { calculateHaversineDistance } from 'utils/shared/geo/calculateHaversineDistance';
import { milesToMeters } from 'utils/shared/geo/milesToMeters';
import { splitNameToFirstLast } from 'utils/shared/name/splitNameToFirstLast';
import { validateEmail } from 'utils/shared/validateEmail';
import { useGeoLocation } from 'hooks/useGeoLocation';
import { useGetIpAddress } from 'hooks/useGetIpAddress';
import { useOAuthAuthentication } from 'hooks/useOAuthAuthentication';
import { getFirebaseErrorCode } from 'hooks/useOAuthSignIn';
import { useViewer } from 'hooks/useViewer';
import BounceLogoSplashShadow from 'svg/BounceLogoSplashShadow';
import { Button } from 'components/Button';
import { ButtonText } from 'components/Button';
import Link from 'components/Link';
import CardError from 'components/cards/CardError';
import AddressSearchEnhanced from 'components/forms/AddressSearch/AddressSearchEnhanced';
import OAuthButtons from 'components/forms/OAuthButtons';
import Switch from 'components/forms/Switch';
import ModalAccountLinking from 'components/modals/ModalAccountLinking';
import classNames from 'styles/utils/classNames';
import ExistingAccountNotice from './ExistingAccountNotice';
import { findBestCityMatch } from './utils';
import { getUtmAttributionVariables } from 'utils/client/attribution';

const IS_USERNAME_INCLUDED = true;
const IS_USERNAME_OPTIONAL = true;
const CITY_DISTANCE_MILES = 100; // Increased from 20 to 100 miles for broader search

const shouldIncludeUsername = (_params: { isCoach: boolean }) => {
  return IS_USERNAME_INCLUDED;
};

interface Props {
  isShowLoginLink?: boolean;
  setAccountTypeParent?: (accountType: AccountType) => void;
  shouldOnboard?: boolean;
  setIsCloseBlocked?: (isCloseBlocked: boolean) => void;
  handleSignupSuccess?: ({ userId }: { userId: string }) => void | Promise<void>;
  toggleLogin?: () => void;
  title?: string;
  cta?: string;
  ignoreText?: string;
  ignoreAction?: () => void;
  defaultEmail?: string;
  defaultName?: string;
  isFormOnly?: boolean;
  formWrapperClassName?: string;
  buttonWrapperClassName?: string;
  spacerClassName?: string;
  shouldSkipReload?: boolean;
  handleAuthComplete?: () => void;
  defaultAccountType?: AccountType;
}

export default function FormSignup({
  isShowLoginLink = false,
  setAccountTypeParent = () => {},
  setIsCloseBlocked = () => {},
  shouldOnboard,
  handleSignupSuccess,
  toggleLogin,
  title = 'Join Bounce',
  cta = 'Coaches, tournaments, and courts near you.',
  ignoreText,
  ignoreAction,
  defaultEmail = '',
  defaultName = '',
  isFormOnly,
  formWrapperClassName,
  buttonWrapperClassName,
  spacerClassName,
  shouldSkipReload,
  handleAuthComplete,
  defaultAccountType,
}: Props) {
  const router = useRouter();
  const viewer = useViewer();
  const client = useApolloClient();
  const {
    position,
    activeAutocompleteAddress,
    suggestedAddresses,
    addressString,
    handleSubmitAutocomplete,
    handleAutcompleteSuggestions,
    setAddressString,
    initializeGeoLocation,
    isLoadingAutocomplete,
    autocompleteInputLength,
    autocompleteMinLength,
    isGoogleMapsAvailable,
  } = useGeoLocation();
  const [cityId, setCityId] = React.useState<null | string>(null);
  const [lastValidAddress, setLastValidAddress] = React.useState<null | string>(addressString);
  const { ipResponse } = useGetIpAddress();
  const {
    authenticateWithProvider,
    isLoading: isOAuthLoading,
    error: oauthError,
    linking,
    linkingError,
    isLinking,
    linkWithExistingProvider,
    linkWithPassword,
    cancelLinking,
  } = useOAuthAuthentication();
  const usernameRef = React.useRef<HTMLInputElement>(null);
  const passwordRef = React.useRef<HTMLInputElement>(null);
  const addressStringRef = React.useRef<string>(addressString || '');
  const [insertSignupRequestQuery, signupRequestResults] = useInsertSignupRequestMutation();
  const [fetchUsernameAvailability, { data, loading, called }] =
    useCheckUsernameAvailabilityLazyQuery();
  const [
    getGroupByAccessCodeLazyQuery,
    {
      data: groupData,
      loading: isLoadingGetGroupByAccessCodeLazyQuery,
      error: errorGetGroupByAccessCodeLazyQuery,
    },
  ] = useGetGroupByAccessCodeLazyQuery();
  const [getClosestCitiesLazyQuery] = useGetClosestCitiesLazyQuery();
  const [insertUserGroupMembershipMutation] = useInsertUserGroupMembershipMutation();
  const [accountType, setAccountType] = React.useState(defaultAccountType || AccountType.Player);
  const [fullName, setFullName] = React.useState(defaultName);
  const [email, setEmail] = React.useState(defaultEmail);
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [_isFocused, setIsFocused] = React.useState(false);
  // TODO: We should probably use a form manager so we're not handling these errors and validations ourselves
  const [emailError, setEmailError] = React.useState('');
  const [passwordError, setPasswordError] = React.useState('');
  const [usernameError, setUsernameError] = React.useState('');
  const [locationError, setLocationError] = React.useState('');
  const [isShowPassword, setIsShowPassword] = React.useState(false);
  const [error, setError] = React.useState<ErrorResponse | null>(null);
  // Set when signup hits `auth/email-already-in-use` — drives the actionable ExistingAccountNotice
  // (which sign-in method(s) the account already uses) instead of a dead-end error message.
  const [existingAccount, setExistingAccount] = React.useState<{
    email: string;
    methods: string[];
  } | null>(null);
  const [requestStatus, setRequestStatus] = React.useState(RequestStatus.Idle);
  const [activePCICertified, setActivePCICertified] = React.useState<boolean>(false);
  const [activePPRCertified, setActivePPRCertified] = React.useState<boolean>(false);
  const [pciId, setPciId] = React.useState('');
  const [pprId, setPprId] = React.useState('');
  const isDisabled =
    /**
     * @note temporarily disable and check on UX
     */
    // cityDataLoading ||
    requestStatus === RequestStatus.InProgress ||
    requestStatus === RequestStatus.Success ||
    viewer.status === AuthStatus.User ||
    isOAuthLoading;
  const isHidden =
    !getIsNativePlatform() &&
    (viewer.status === AuthStatus.Loading || viewer.status === AuthStatus.User) &&
    !error &&
    requestStatus !== RequestStatus.InProgress &&
    requestStatus !== RequestStatus.Success;
  const isCoach = accountType === AccountType.Coach;
  const isAvailableUsername = !data?.usernamesActive?.length && !data?.usernamesClaimed?.length;
  const isValidUsername = getIsValidUsernameCaseIncensitiveFormat(username);
  const isUsernameAllowed = isAvailableUsername && isValidUsername;
  const isUsernameInputFocused =
    typeof window !== 'undefined' && usernameRef?.current === document.activeElement;
  const isPasswordInputFocused =
    typeof window !== 'undefined' && passwordRef?.current === document.activeElement;
  const group = groupData?.groups[0];

  React.useEffect(() => {
    if (defaultAccountType) {
      setAccountType(defaultAccountType);
    }
  }, [defaultAccountType]);

  // Initialize geolocation when form opens
  React.useEffect(() => {
    if (!position) {
      initializeGeoLocation();
    }
  }, []);

  React.useEffect(() => {
    if (router.isReady && position) {
      const addressString = addressStringRef.current;
      getClosestCitiesLazyQuery({
        variables: {
          distance: milesToMeters(CITY_DISTANCE_MILES),
          from: {
            type: 'Point',
            coordinates: [position.longitude, position.latitude],
          },
        },
      }).then((response) => {
        const cities = response?.data?.cities || [];

        // One function call handles everything: matching, distance calculation, and fallback
        const bestMatch = findBestCityMatch(addressString, cities, position);

        if (bestMatch) {
          setCityId(bestMatch.id);
        }
      });
    }
  }, [router.isReady, position]);

  React.useEffect(() => {
    const fetchGroup = async (groupCode: string) => {
      getGroupByAccessCodeLazyQuery({
        variables: {
          accessCode: groupCode,
        },
      });
    };

    if (router.isReady) {
      const groupCode = router.query.code as string;

      if (groupCode) {
        fetchGroup(groupCode);
      }
    }
  }, [router.isReady]);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();

    if (requestStatus === RequestStatus.InProgress || requestStatus === RequestStatus.Success) {
      return;
    }

    // Check for fraud before proceeding
    const fraudDetection = detectFraudFromBrowser();
    if (fraudDetection.isFraud) {
      Sentry.captureException(new Error('Fraud attempt detected during signup'), {
        extra: {
          reason: fraudDetection.reason,
          timezone: fraudDetection.timezone,
          locale: fraudDetection.locale,
          email,
          fullName,
          accountType,
        },
      });
      setError({ message: 'Unable to create account. Please try again later.' });
      setRequestStatus(RequestStatus.Error);
      setIsCloseBlocked(false);
      return;
    }

    setIsCloseBlocked(true);
    setRequestStatus(RequestStatus.InProgress);
    setError(null);
    setExistingAccount(null);
    setEmailError('');
    setPasswordError('');
    setUsernameError('');
    setLocationError('');

    const submissionUsername = username.toLowerCase().trim();
    const didChooseNoUsername = IS_USERNAME_OPTIONAL && !submissionUsername;

    try {
      const isValidEmail = validateEmail(email);
      const isValidUsername = getIsValidUsernameFormat(submissionUsername);
      const isValidPassword = getIsValidPasswordFormat(password);
      const claimedUsernameResponse =
        shouldIncludeUsername({ isCoach }) && !didChooseNoUsername
          ? await fetchUsernameAvailability({
              variables: {
                username: submissionUsername,
              },
            })
          : null;
      const isUsernameUnavailable =
        shouldIncludeUsername({ isCoach }) && !didChooseNoUsername
          ? (claimedUsernameResponse?.data?.usernamesClaimed?.length || 0) > 0 ||
            (claimedUsernameResponse?.data?.usernamesActive?.length || 0) > 0
          : false;
      const isValidLocation =
        !!position && !!position.latitude && !!position.longitude && !!addressString;

      if (!isValidEmail) {
        setEmailError('Please enter a valid email address');
      }
      if (isUsernameUnavailable) {
        setUsernameError('Not available');
      }
      if (!isValidUsername && shouldIncludeUsername({ isCoach }) && !didChooseNoUsername) {
        setUsernameError('Invalid username format');
      }
      if (!isValidPassword) {
        setPasswordError(
          password.length < MINIMUM_PASSWORD_LENGTH
            ? 'Password must be at least 8 characters'
            : 'Invalid password format',
        );
      }
      if (!isValidLocation) {
        setLocationError('Please enter a valid location');
      }

      if (
        !isValidEmail ||
        (!isValidUsername && shouldIncludeUsername({ isCoach }) && !didChooseNoUsername) ||
        !isValidPassword ||
        isUsernameUnavailable ||
        !isValidLocation
      ) {
        setRequestStatus(RequestStatus.Idle);
        setIsCloseBlocked(false);
        return;
      }
    } catch (error) {
      Sentry.captureException(error);
      setRequestStatus(RequestStatus.Error);
      setIsCloseBlocked(false);
      // @ts-ignore
      setError(error);
      return;
    }

    try {
      await insertSignupRequestQuery({
        variables: {
          email: email.toLowerCase(),
          username: submissionUsername,
          fullName,
          preferredName: splitNameToFirstLast(fullName).firstName,
          accountType,
          ip: ipResponse?.ip || '',
          country: ipResponse?.country || '',
          region: ipResponse?.region || '',
          city: ipResponse?.city || '',
          fullDetails: ipResponse || null,
          platform: getPlatform(),
          // NOTE: These fields are from a previously paid API
          timezone: '', // I can get timezone in JS
          zip: '',
          cityId: cityId || null,
          latitude: position?.latitude || null,
          longitude: position?.longitude || null,
          ...getUtmAttributionVariables(),
        },
      });
    } catch (error) {
      Sentry.captureException(error);
      setRequestStatus(RequestStatus.Error);
      setIsCloseBlocked(false);
      // @ts-ignore
      setError(error);
      return;
    }

    const OVERALL_TIMEOUT_MS = 45_000;
    let overallTimeoutId: ReturnType<typeof setTimeout>;
    const overallTimeout = new Promise<never>((_, reject) =>
      overallTimeoutId = setTimeout(() => reject(new Error('Account setup timed out. Please refresh the page — your account may already be created.')), OVERALL_TIMEOUT_MS),
    );

    const signupFlow = async () => {
      try {
        Sentry.addBreadcrumb({ category: 'signup', message: 'Creating Firebase user' });
        const user = await createUserWithEmailAndPassword(auth, email, password);
        setRequestStatus(RequestStatus.Success);

        sendEmailVerification(user.user);

        Sentry.addBreadcrumb({ category: 'signup', message: 'Polling for new user in DB' });
        const databaseUser = (await pollForNewUser(
          client,
          user.user,
        )) as GetExistingUserQuery['users'][0];

        if (handleSignupSuccess && databaseUser.id) {
          await handleSignupSuccess({ userId: databaseUser.id });
        }

        facebookTrackCompleteRegistration({
          email,
          fullName,
          username: databaseUser.username,
          userId: databaseUser.id,
          isCoach: accountType === AccountType.Coach,
          accountType,
          coachStatus: databaseUser.coachStatus,
        });

        identify({
          userId: databaseUser.id,
          email,
          name: fullName,
          additionalUserParams: {
            username: databaseUser.username,
            accountType,
          },
        });
        trackEvent(SIGNUP_COMPLETED_EVENT, {
          accountType,
          isCoach: accountType === AccountType.Coach,
        });

        const groupId = groupData?.groups[0]?.id;
        if (groupId && databaseUser) {
          await insertUserGroupMembershipMutation({
            variables: {
              userId: databaseUser.id,
              groupId: groupId,
            },
          });
        }

        setIsCloseBlocked(false);

        if (isCoach) {
          router.push(COACH_HOME_PAGE);
        } else if (shouldOnboard) {
          const redirectUrl = isCoach ? COACH_HOME_PAGE : ROOT_PAGE;
          router.push(redirectUrl);
        } else if (!shouldSkipReload) {
          router.reload();
        } else if (handleAuthComplete) {
          handleAuthComplete();
        }
      } finally {
        clearTimeout(overallTimeoutId);
      }
    };

    Promise.race([signupFlow(), overallTimeout]).catch(async function (errorResponse) {
      // Email/password signup collided with an existing account. Surface the actual sign-in
      // method(s) the account uses as one-tap actions instead of a dead-end "log in instead"
      // message. fetchSignInMethodsForEmail may return [] under email-enumeration protection —
      // the notice falls back to offering every option in that case.
      if (getFirebaseErrorCode(errorResponse) === 'auth/email-already-in-use') {
        const normalizedEmail = email.toLowerCase().trim();
        const methods = await fetchSignInMethodsForEmail(auth, normalizedEmail).catch(
          () => [] as string[],
        );
        setRequestStatus(RequestStatus.Error);
        setIsCloseBlocked(false);
        setExistingAccount({ email: normalizedEmail, methods });
        return;
      }

      Sentry.captureException(errorResponse instanceof Error ? errorResponse : new Error('Sign up / poll did not reach success state'), {
        extra: { originalError: errorResponse },
      });
      setRequestStatus(RequestStatus.Error);
      setIsCloseBlocked(false);
      setError({ message: getSignupErrorMessage(errorResponse) });
    });
  };

  // Mirrors the email-signup success redirect; used when an EXISTING user signs in via OAuth.
  // Brand-new OAuth users skip this — the global ModalCompleteProfile takes over instead.
  const redirectAfterSignup = async (dbUser: { id: string } | null | undefined) => {
    if (handleSignupSuccess && dbUser?.id) {
      await handleSignupSuccess({ userId: dbUser.id });
    }

    setIsCloseBlocked(false);

    if (isCoach) {
      router.push(COACH_HOME_PAGE);
    } else if (shouldOnboard) {
      const redirectUrl = isCoach ? COACH_HOME_PAGE : ROOT_PAGE;
      router.push(redirectUrl);
    } else if (!shouldSkipReload) {
      router.reload();
    } else if (handleAuthComplete) {
      handleAuthComplete();
    }
  };

  const handleProviderSelect = (provider: SocialAuthProvider) => {
    setIsCloseBlocked(true);
    authenticateWithProvider(provider, {
      statePayload: {
        initialLat: position?.latitude,
        initialLng: position?.longitude,
        username: username.trim() || undefined,
        accountType,
      },
      onExistingUser: redirectAfterSignup,
      returnTo: router.asPath,
    });
  };

  // Route an existing email/password user to the login flow (toggle in-place when embedded in a
  // modal that supports it, otherwise navigate to the login page prefilled with their email).
  const goToLogin = () => {
    setIsCloseBlocked(false);
    if (toggleLogin) {
      toggleLogin();
      return;
    }
    const normalizedEmail = email.toLowerCase().trim();
    router.push(
      normalizedEmail ? `${LOGIN_PAGE}?email=${encodeURIComponent(normalizedEmail)}` : LOGIN_PAGE,
    );
  };

  return (
    <>
      <form
        className="flex h-full w-full grow flex-col items-center lg:justify-center"
        onSubmit={handleSubmit}
      >
        <div className="flex h-full w-full grow flex-col items-center overflow-y-auto lg:h-auto lg:grow-0">
          {!isFormOnly && (
            <>
              {/* {shouldOnboard ? (
                <div
                  className={classNames(
                    'flex grow flex-col items-center justify-center pt-5 transition-opacity',
                    !group ? 'opacity-0' : 'opacity-100',
                  )}
                >
                  <p className="mb-3 text-sm text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
                    Welcome to
                  </p>
                  <h1 className="text-2xl font-semibold text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                    {group?.title || 'Bounce'}
                  </h1>
                  <h2 className="mt-[1px] font-medium">with {group?.ownerUserProfile?.fullName}</h2>
                </div>
              ) : ( */}
              <>
                <h1 className="flex shrink-0 items-center justify-center space-x-4 px-4 pt-12 text-center sm:px-[6.25rem] lg:pt-10">
                  <span className="text-2xl font-semibold text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                    <BounceLogoSplashShadow className="h-20" aria-label="Bounce Logo" />
                  </span>
                </h1>
                <h2 className="typography-product-display mt-1 text-pretty px-2 text-center text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                  {isCoach ? 'Join Bounce' : title}
                </h2>
                <h3 className="typography-product-body mt-ds-md text-pretty px-2 text-center text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                  {isCoach ? 'Grow your coaching business and make an impact.' : cta}
                </h3>
              </>
              {/* )} */}
            </>
          )}
          <div
            className={classNames(
              'flex w-full flex-auto flex-col items-center justify-center transition-opacity duration-700',
              !isFormOnly && 'px-4 py-4 sm:px-[3.25rem]',
              !!formWrapperClassName && formWrapperClassName,
              isHidden ? 'opacity-0' : 'opacity-100',
            )}
          >
            {/* <h2 className="font mb-6 text-xl lg:mt-4">Create your account</h2> */}
            {!isFormOnly && (
              <div className="mb-4 flex w-full md:w-2/3">
                <div className="flex w-full rounded-3xl bg-color-bg-input-lightmode-primary p-1 shadow-sm dark:bg-color-bg-input-darkmode-primary">
                  <div className="relative flex w-full text-center">
                    <div
                      className={classNames(
                        'absolute left-0 h-full w-1/2 rounded-3xl bg-color-bg-lightmode-brand shadow-sm transition-transform',
                        isCoach ? 'translate-x-full' : 'translate-x-0',
                        isDisabled && 'bg-opacity-50',
                      )}
                    >
                      &nbsp;
                    </div>
                    <button
                      onClick={() => {
                        setAccountType(AccountType.Player);
                        setAccountTypeParent(AccountType.Player);
                      }}
                      type="button"
                      disabled={isDisabled}
                      className={classNames(
                        'relative h-10 w-1/2 rounded-full',
                        !isCoach
                          ? 'bg-color-bg-lightmode-brand text-color-text-darkmode-primary'
                          : 'text-color-text-lightmode-primary dark:text-color-text-darkmode-primary',
                      )}
                    >
                      Player
                    </button>
                    <button
                      onClick={() => {
                        setAccountType(AccountType.Coach);
                        setAccountTypeParent(AccountType.Coach);
                      }}
                      type="button"
                      disabled={isDisabled}
                      className={classNames(
                        'relative h-10 w-1/2 rounded-full',
                        isCoach
                          ? 'bg-color-bg-lightmode-brand text-color-text-darkmode-primary'
                          : 'text-color-text-lightmode-primary dark:text-color-text-darkmode-primary',
                      )}
                    >
                      Coach
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div
              className={classNames(
                'flex w-full flex-col',
                spacerClassName ? spacerClassName : 'gap-5',
              )}
            >
              <div className="w-full">
                <label className="sr-only" htmlFor="name">
                  First and last name
                </label>
                <input
                  id="name"
                  name="name"
                  autoComplete="name"
                  type="text"
                  value={fullName}
                  onFocus={() => setError(null)}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="First and last name"
                  disabled={isDisabled}
                  className="input-form"
                  required
                />
              </div>
              <div className="w-full">
                <AddressSearchEnhanced
                  id="location"
                  name="location"
                  autoComplete="off"
                  type="text"
                  disabled={isDisabled}
                  onBlur={() => {
                    if (lastValidAddress && lastValidAddress !== addressString) {
                      setAddressString(lastValidAddress);
                    }
                  }}
                  onFocus={() => {
                    setError(null);
                    setLocationError('');
                  }}
                  handleSubmit={(a) => {
                    addressStringRef.current = a?.description || addressString;
                    setLastValidAddress(a?.description || addressString);
                    handleSubmitAutocomplete(a, false);
                  }}
                  handleAutcompleteSuggestions={handleAutcompleteSuggestions}
                  addressString={addressString}
                  suggestedAddresses={suggestedAddresses.slice(0, 3)}
                  activeAutocompleteAddress={activeAutocompleteAddress}
                  className={classNames('input-form', !!locationError && 'text-color-error')}
                  placeholder="City or zip"
                  isLoadingAutocomplete={isLoadingAutocomplete}
                  autocompleteInputLength={autocompleteInputLength}
                  autocompleteMinLength={autocompleteMinLength}
                  isGoogleMapsAvailable={isGoogleMapsAvailable}
                />
                {!!locationError && (
                  <div className="mt-2 max-w-xs text-xs text-color-error">{locationError}</div>
                )}
              </div>
              <div className="w-full">
                <label className="sr-only" htmlFor="email">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  autoComplete="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => {
                    setError(null);
                    setExistingAccount(null);
                    setEmailError('');
                  }}
                  placeholder="Email"
                  disabled={isDisabled}
                  className={classNames('input-form', !!emailError && 'text-color-error')}
                  required
                />
                {!!emailError && (
                  <div className="mt-2 max-w-xs text-xs text-color-error">{emailError}</div>
                )}
              </div>
              {shouldIncludeUsername({ isCoach }) && (
                <div className="w-full">
                  <label className="sr-only" htmlFor="username">
                    Username (optional)
                  </label>
                  <input
                    id="username"
                    name="username"
                    autoComplete="username"
                    autoCapitalize="off"
                    type="text"
                    value={username}
                    ref={usernameRef}
                    onChange={(e) => setUsername(e.target.value.trim())}
                    onFocus={() => {
                      setError(null);
                      setIsFocused(true);
                      setUsernameError('');
                    }}
                    onBlur={() => {
                      setIsFocused(false);
                      fetchUsernameAvailability({
                        variables: {
                          username: username.toLowerCase(),
                        },
                      });
                    }}
                    placeholder="Username (optional)"
                    disabled={isDisabled}
                    className={classNames('input-form', !!usernameError && 'text-color-error')}
                    minLength={4}
                    maxLength={36}
                    required={!IS_USERNAME_OPTIONAL}
                  />
                  <div className="mt-2 flex text-xs">
                    <div className="mr-2">
                      {!username && IS_USERNAME_OPTIONAL ? (
                        <UserCircleIcon className="h-4 w-4 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary" />
                      ) : (
                        <>
                          {(isUsernameInputFocused || (!called && !loading)) && (
                            <UserCircleIcon className="h-4 w-4 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary" />
                          )}
                          {!isUsernameInputFocused && loading && (
                            <EllipsisHorizontalCircleIcon className="h-4 w-4 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary" />
                          )}
                          {!isUsernameInputFocused &&
                            !loading &&
                            called &&
                            !isUsernameAllowed &&
                            (username || !IS_USERNAME_OPTIONAL) && (
                              <ExclamationCircleIcon className="h-4 w-4 text-color-error" />
                            )}
                          {!isUsernameInputFocused && !loading && called && isUsernameAllowed && (
                            <CheckCircleIcon className="h-4 w-4 text-color-success" />
                          )}
                        </>
                      )}
                    </div>
                    <div className="text-xs text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                      {((!usernameError && !loading && isAvailableUsername && !isValidUsername) ||
                        !called) &&
                        'Minimum 4 characters. Letters, numbers, and underscores only.'}
                      {!usernameError &&
                        !isUsernameInputFocused &&
                        !loading &&
                        isUsernameAllowed &&
                        'Available'}
                      {!usernameError &&
                        !isUsernameInputFocused &&
                        !loading &&
                        !isAvailableUsername &&
                        'Not available'}
                      {!!usernameError && <span className="text-color-error">{usernameError}</span>}
                    </div>
                  </div>
                </div>
              )}
              <div className="relative w-full">
                <label className="sr-only" htmlFor="password">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    autoComplete="new-password"
                    type={isShowPassword ? 'text' : 'password'}
                    value={password}
                    ref={passwordRef}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => {
                      setError(null);
                      setIsFocused(true);
                      setPasswordError('');
                    }}
                    onBlur={() => setIsFocused(false)}
                    minLength={MINIMUM_PASSWORD_LENGTH}
                    placeholder="Create Password"
                    disabled={isDisabled}
                    className={classNames(
                      'input-form ph-no-autocapture',
                      !!passwordError && 'text-color-error',
                    )}
                    required
                  />
                  <div className="absolute right-4 top-3 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                    {isShowPassword ? (
                      <button
                        className="h-4 w-4"
                        type="button"
                        onClick={() => setIsShowPassword(false)}
                      >
                        <EyeIcon />
                      </button>
                    ) : (
                      <button type="button" onClick={() => setIsShowPassword(true)}>
                        <EyeSlashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
                {!!passwordError && (
                  <div className="mt-2 flex max-w-xs text-xs">
                    <div className="mr-2">
                      {(!password?.length ||
                        (isPasswordInputFocused && !getIsValidPasswordFormat(password))) && (
                        <InformationCircleIcon className="h-4 w-4 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary" />
                      )}
                      {!isPasswordInputFocused &&
                        !!password?.length &&
                        !getIsValidPasswordFormat(password) && (
                          <ExclamationCircleIcon className="h-4 w-4 text-color-error" />
                        )}
                      {getIsValidPasswordFormat(password) && (
                        <CheckCircleIcon className="h-4 w-4 text-color-success" />
                      )}
                    </div>
                    <div className="max-w-xs text-xs text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                      {passwordError && <span className="text-color-error">{passwordError}</span>}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div
          className={classNames(
            'w-full shrink-0 transition-opacity duration-700',
            isHidden ? 'opacity-0' : 'opacity-100',
          )}
        >
          <div className={classNames('w-full', !isFormOnly && 'px-4 pb-6 sm:px-[6.25rem]')}>
            {existingAccount ? (
              <div className="mb-4">
                <ExistingAccountNotice
                  email={existingAccount.email}
                  existingMethods={existingAccount.methods}
                  disabled={isDisabled}
                  onUseProvider={handleProviderSelect}
                  onLogin={goToLogin}
                />
              </div>
            ) : (
              !!(error?.message || oauthError) && (
                <div className="mb-4">
                  <CardError>{error?.message || oauthError?.message}</CardError>
                </div>
              )
            )}
            <div className="px-6 py-5">
              <div className="w-full pb-8">
                <Button variant="brand" size="lg" type="submit" disabled={isDisabled}>
                  {isDisabled ? 'Creating account...' : 'Sign up'}
                </Button>
              </div>

              <div className="mb-6 flex items-center gap-3">
                <span className="h-px flex-1 bg-color-border-input-lightmode dark:bg-color-border-input-darkmode" />
                <span className="typography-product-caption text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
                  or
                </span>
                <span className="h-px flex-1 bg-color-border-input-lightmode dark:bg-color-border-input-darkmode" />
              </div>
              <div className={classNames('pb-8', !isFormOnly && 'sm:-mx-12')}>
                <OAuthButtons onSelect={handleProviderSelect} disabled={isDisabled} />
              </div>

              {isShowLoginLink && (
                <div className="text-center text-sm">
                  {/* <span className="typography-product-element-label block pb-8 font-normal text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                    By registering, you will create a Bounce account.
                  </span> */}
                  <span className="typography-product-button-label-medium pr-2 font-normal text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                    Already have an account?
                  </span>{' '}
                  {isDisabled ? (
                    <span className="font-medium text-color-brand-highlight opacity-50">
                      Log in
                    </span>
                  ) : toggleLogin ? (
                    <button
                      onClick={() => toggleLogin()}
                      type="button"
                      className="typography-product-button-label-medium font-normal text-color-brand-highlight"
                    >
                      Log in
                    </button>
                  ) : (
                    <Link href={LOGIN_PAGE} className="font-medium text-color-brand-highlight">
                      Log in
                    </Link>
                  )}
                </div>
              )}
            </div>
            {!!ignoreText && !!ignoreAction && (
              <div className="mt-6 text-center">
                <ButtonText
                  onClick={ignoreAction}
                  className="typography-product-button-label-medium text-color-text-brand"
                >
                  {ignoreText}
                </ButtonText>
              </div>
            )}
          </div>
        </div>
      </form>
      {linking && (
        <ModalAccountLinking
          email={linking.email}
          newProvider={linking.newProvider}
          existingMethods={linking.existingMethods}
          isLinking={isLinking}
          error={linkingError}
          onLinkWithExistingProvider={linkWithExistingProvider}
          onLinkWithPassword={linkWithPassword}
          onCancel={cancelLinking}
        />
      )}
    </>
  );
}
